"use client"

import {useEffect, useRef, useState} from "react"
import {Dialog, DialogContent, DialogTitle} from "@/components/ui/dialog"
import {Button} from "@/components/ui/button"
import {VisuallyHidden} from "@radix-ui/react-visually-hidden"
import {useBoundStore} from "@/hooks/queries/stores/use-bound-store"
import {useShallow} from "zustand/react/shallow"
import {gaId} from "@/lib/analytics/clickTracker"
import {Check} from "lucide-react"
import Link from "next/link"
import {profileBillingPath} from "@/app/paths";
import {Separator} from "@/components/ui/separator";

const STORAGE_KEY = "trialExpiredModalDismissed"

interface TrialExpiredModalProps {
  onClose?: () => void
}

export function TrialExpiredModal({onClose}: TrialExpiredModalProps) {
  const {trialInfo} = useBoundStore(useShallow(s => ({
    trialInfo: s.ownTrialInfo,
    limits: s.ownLimits,
  })))
  const forceModal = useBoundStore(s => s.forceTrialModal)
  const {setForceTrialModal} = useBoundStore()
  const [isOpen, setIsOpen] = useState(false)
  const [delayPassed, setDelayPassed] = useState(false)
  const wasOpen = useRef(false)

  const expired = trialInfo?.expired
  const skip = trialInfo?.skipTrialNotification;
  const shouldShowModal = !skip && expired && (!getModalDismissedThisSession() || forceModal)

  useEffect(() => {
    if (wasOpen.current && !forceModal) {
      return
    }
    if (shouldShowModal && delayPassed) {
      setIsOpen(true)
      wasOpen.current = true
    }
  }, [shouldShowModal, delayPassed, forceModal, setForceTrialModal])

  useEffect(() => {
    const timer = setTimeout(() => {
      setDelayPassed(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, [])

  const handleClose = () => {
    setIsOpen(false)
    setModalDismissedThisSession()
    setForceTrialModal(false)
    onClose?.()
  }

  return (
    <Dialog open={isOpen && shouldShowModal} onOpenChange={handleClose}>
      <DialogContent className="h-full max-h-[500px] p-0 overflow-hidden gap-0"
                     style={{width: "100%", maxWidth: "900px"}}>
        <VisuallyHidden>
          <DialogTitle>Trial Ended - Your Devplan trial has ended</DialogTitle>
        </VisuallyHidden>
        <div className="flex h-full overflow-y-auto">
          <div className="w-1/2 p-8 lg:p-10 flex flex-col justify-center space-y-6">
            <div className="space-y-4">
              <div className="text-lg font-bold text-blue-500">
                TRIAL ENDED
              </div>

              <div className="space-y-8">
                <h1 className="text-3xl lg:text-4xl font-bold tracking-tight">
                  Your Devplan trial has ended.
                </h1>
                <p className="text-lg text-muted-foreground">
                  Want to keep building with Devplan? Choose a plan to
                  unlock full access and keep your product momentum going!
                </p>
              </div>
            </div>

            <div className="space-y-8">
              <div>
                <Link href={profileBillingPath()} onClick={handleClose}>
                  <Button
                    size="lg"
                    className="w-full lg:w-auto text-base px-8 bg-blue-500 hover:bg-blue-600"
                    {...gaId("trial_expired_view_plans")}
                  >
                    View Plans
                  </Button>
                </Link>
              </div>

              <Separator/>

              <div className="space-y-2">
                <div className="font-semibold">
                  Have any questions?
                </div>
                <div className="text-muted-foreground">
                  Contact us at{" "}
                  <a
                    href="mailto:info@devplan.com"
                    className="text-primary hover:underline"
                    {...gaId("trial_expired_contact_support")}
                  >
                    info@devplan.com
                  </a>
                </div>

              </div>
            </div>
          </div>

          <div className="w-1/2 h-full text-background text-3xl">
            <div
              className="h-full relative flex-1 bg-gradient-to-br from-[#4F46E5] via-[#A78BFA] to-[#06B6D4] overflow-hidden">
              <div className="p-8 flex justify-center items-center h-full">
                <div>
                  <div className="space-y-4">
                    <h2 className="font-bold text-bg">
                      Unlock the full potential of Devplan
                    </h2>

                    <div className="space-y-3">
                      <ValueProp
                        text="Instantly turn ideas into structured build plans"
                      />
                      <ValueProp
                        text="Auto-generate detailed prompts for AI coding"
                      />
                      <ValueProp
                        text="Get a contextually-aware virtual planning partner"
                      />
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function ValueProp({text}: { text: string }) {
  return (
    <div className="flex items-center gap-3 text-left">
      <Check className="sie-5"/>
      <span className="text-lg font-medium">{text}</span>
    </div>
  )
}

// Helper functions for session storage
function getModalDismissedThisSession(): boolean {
  if (typeof window === "undefined") return false

  const dismissed = sessionStorage.getItem(STORAGE_KEY)
  return dismissed === "true"
}

function setModalDismissedThisSession(): void {
  if (typeof window === "undefined") return

  sessionStorage.setItem(STORAGE_KEY, "true")
}
